import React, { useState } from 'react'
import { DataCard } from '@/components/DataCard/DataCard'
import { FormReadonlyField, FormSelectField } from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldGroup,
  FieldLabel,
  FieldTitle,
} from '@/components/ui/field'
import { Spinner } from '@/components/ui/spinner'
import { Switch } from '@/components/ui/switch'
import { DateFormatType } from 'api/Identity/Types/DateFormatType'
import { UpdateUserProfile } from 'api/Identity/Types/UpdateUserProfile'
import { UserSession } from 'api/Identity/Types/UserSession'
import { Form, FormikProps, withFormik } from 'formik'
import { FormSubmitValues } from 'utils/formValidation'
import { isUIMenuPosition, UIMenuPosition } from 'utils/persistentPreferences'
import { TimeZone } from 'utils/time-zone/types'
import { useUIVersion } from 'utils/UIVersionContext'

import { isQAMode, QA_MODE_KEY, QAModeState } from '../../store/store'

const menuPositionOptions: Array<{ id: UIMenuPosition; name: string }> = [
  { id: 'left', name: 'Left' },
  { id: 'right', name: 'Right' },
  { id: 'top', name: 'Top' },
]

const UserProfileSettingsFormUI: React.FC<FormikProps<UpdateUserProfile> & OuterProps> = (
  props
) => {
  const {
    handleSubmit,
    setSubmitting,
    isSubmitting,
    setFieldValue: setFormikFieldValue,
    user,
  } = props
  const { isNewUI, menuPosition, enableNewUI, disableNewUI, setMenuPosition } = useUIVersion()
  const [newUi, setNewUi] = useState(isNewUI)
  const [selectedMenuPosition, setSelectedMenuPosition] = useState<UIMenuPosition>(menuPosition)
  const [qaMode, setQaMode] = useState(isQAMode)

  const handleSubmitForm = () => {
    setSubmitting(true)
    handleSubmit()
    if (newUi !== isNewUI) {
      if (newUi) enableNewUI()
      else disableNewUI()
    }
    if (newUi && selectedMenuPosition !== menuPosition) {
      setMenuPosition(selectedMenuPosition)
    }
  }

  return (
    <Form>
      <DataCard title='Settings'>
        <FormReadonlyField label='First Name' value={user.firstName} />
        <FormReadonlyField label='Last Name' value={user.lastName} />
        <FormReadonlyField label='E-mail' value={user.email} />
        <FormSelectField
          options={props.dateFormat}
          label='Date Format'
          name='dateFormatTypeId'
          onChange={(value) => setFormikFieldValue('dateFormatTypeId', Number(value))}
        />
        <FormSelectField
          options={props.timeZones.map((tz) => ({ ...tz, name: `${tz.name} (UTC ${tz.offset})` }))}
          label='Time Zone'
          showSearch
          name='timeZoneId'
          onChange={(value) => setFormikFieldValue('timeZoneId', String(value))}
        />

        <FieldGroup className='w-full gap-4 rounded-md border p-3'>
          <Field orientation='horizontal'>
            <FieldContent>
              <FieldLabel htmlFor='new-ui' className='w-fit'>
                <FieldTitle>Use New UI</FieldTitle>
              </FieldLabel>
              <FieldDescription>Toggle between the new and legacy UI.</FieldDescription>
            </FieldContent>
            <Switch id='new-ui' checked={newUi} onCheckedChange={setNewUi} />
          </Field>

          {newUi && (
            <div className='border-t pt-4'>
              <FormSelectField
                options={menuPositionOptions}
                label='Menu Position'
                name='menuPosition'
                value={selectedMenuPosition}
                storeValueAs='value'
                onChange={(value) => {
                  if (isUIMenuPosition(value)) {
                    setSelectedMenuPosition(value)
                  }
                }}
              />
            </div>
          )}
        </FieldGroup>

        <FieldGroup className='w-full'>
          <FieldLabel htmlFor='qa-mode'>
            <Field orientation='horizontal'>
              <FieldContent>
                <FieldTitle>QA Mode</FieldTitle>
                <FieldDescription>
                  Disable redux cache. Forces all data to be fetched fresh.
                </FieldDescription>
              </FieldContent>
              <Switch
                id='qa-mode'
                checked={qaMode}
                onCheckedChange={(checked) => {
                  setQaMode(checked)
                  if (checked) {
                    localStorage.setItem(QA_MODE_KEY, QAModeState.Enabled)
                    localStorage.removeItem('persist:root')
                  } else {
                    localStorage.setItem(QA_MODE_KEY, QAModeState.Disabled)
                  }
                  window.location.reload()
                }}
              />
            </Field>
          </FieldLabel>
        </FieldGroup>

        <Button className='mx-auto' disabled={isSubmitting} size='lg' onClick={handleSubmitForm}>
          Save Changes
          {isSubmitting && <Spinner />}
        </Button>
      </DataCard>
    </Form>
  )
}

interface OuterProps {
  user: UserSession
  dateFormat: DateFormatType[]
  timeZones: TimeZone[]
  timeZoneId: string
  onSubmit(values: FormSubmitValues<UpdateUserProfile>): Promise<void>
}

export const UserProfileSettingsForm = withFormik<OuterProps, UpdateUserProfile>({
  mapPropsToValues: ({ user, timeZoneId }) => {
    return {
      ...user,
      dateFormatTypeId: user.dateFormatType.id,
      timeZoneId,
    }
  },

  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      await props.onSubmit(values)
    } finally {
      setSubmitting(false)
    }
  },

  enableReinitialize: true,
})(UserProfileSettingsFormUI)
